import type { GameState } from '../state';
import { D, add, sub, gte, type Decimal } from '../math/num';
import { chance, weightedIndex } from '../math/rng';
import { getFrame } from '../data/frames';
import {
  getEscalation,
  maxEscalationTier,
  sortieComponents,
  repairCost,
  buildDroneCost,
  bayUpgradeCost,
  cacheRarityWeights,
  RARITY_LIST,
  START_DRONES,
  MAX_DRONES,
} from '../data/drones';
import { droppableDefs } from '../data/parts/partDefs';
import { rollPartInstance } from '../data/parts/roll';
import type { PartInstance } from '../data/parts/partTypes';

/**
 * Salvage Drone Bay sim (data/drones.ts holds the numbers). Pure & deterministic:
 * sorties resolve off elapsedMs and roll rewards via the seeded RNG, so they
 * advance identically online and during offline replay. The Interlock: a sortie's
 * Components feed MechMaxxing upgrades; its Cache is a part you can't farm passively.
 */

function frameTier(state: GameState): number {
  return getFrame(state.frame.defId).tier;
}

/** Bay capacity: the base maximum plus Smelter Bay Extension ranks. */
export function maxDrones(state: GameState): number {
  return MAX_DRONES + (state.smelter.upgrades['su.bay'] ?? 0);
}

/** Drones free to launch right now (total − broken − any committed to a sortie). */
export function availableDrones(state: GameState): number {
  const dr = state.drones;
  const committed = dr.sortie ? dr.sortie.dronesSent : 0;
  return Math.max(0, dr.bots - dr.broken - committed);
}

/** The Tech Find's FIELD benefit: drones not out on a sortie hover over the
 *  battlefield and auto-loot, adding +8% Scrap per available drone. */
export const DRONE_SCRAP_BONUS = 0.08;
export function droneScrapBonus(state: GameState): Decimal {
  if (!state.drones.unlocked) return D(1);
  return D(1 + DRONE_SCRAP_BONUS * availableDrones(state));
}

/** Resolve a returned sortie's payout (called when a running sortie's timer elapses). */
export function stepDrones(state: GameState, _dtMs: number): void {
  const dr = state.drones;
  const s = dr.sortie;
  if (!dr.unlocked || !s || s.status !== 'running') return;
  if (state.elapsedMs < s.endsMs) return;

  const esc = getEscalation(s.tier);
  if (!esc) { dr.sortie = null; return; }
  const tier = frameTier(state);

  const components = sortieComponents(dr.clearance, s.tier, s.dronesSent, tier);

  // Each committed drone independently risks breaking.
  let broke = 0;
  for (let i = 0; i < s.dronesSent; i++) if (esc.riskPct > 0 && chance(state.rng, esc.riskPct)) broke++;

  // Cache: a guaranteed part, biased rarer than wave drops.
  let cache: PartInstance | null = null;
  if (esc.cachePct > 0 && chance(state.rng, esc.cachePct)) {
    const pool = droppableDefs(tier);
    if (pool.ids.length > 0) {
      const rarity = RARITY_LIST[weightedIndex(state.rng, cacheRarityWeights(dr.clearance, s.tier))] ?? 'common';
      const defId = pool.ids[weightedIndex(state.rng, pool.weights)] ?? pool.ids[0]!;
      const { instance, nextId } = rollPartInstance(defId, rarity, state.rng, state.inventory.nextId);
      state.inventory.nextId = nextId; // reserve id deterministically (added to inventory on collect)
      cache = instance;
    }
  }

  s.status = 'returned';
  s.result = { components, cache, broke };
}

export interface DroneOpResult {
  ok: boolean;
  reason?: string;
}

export function launchSortie(state: GameState, tier: number, drones: number): DroneOpResult {
  const dr = state.drones;
  if (!dr.unlocked) return { ok: false, reason: 'Drone Bay not online.' };
  if (dr.sortie) return { ok: false, reason: 'A sortie is already out. Collect it first.' };
  if (tier < 1 || tier > maxEscalationTier(dr.clearance)) return { ok: false, reason: 'Escalation Tier exceeds Clearance.' };
  const avail = availableDrones(state);
  if (avail < 1) return { ok: false, reason: 'No drones available.' };
  const n = Math.max(1, Math.min(Math.floor(drones), avail));
  const esc = getEscalation(tier);
  if (!esc) return { ok: false, reason: 'Unknown sortie.' };
  dr.sortie = { tier, dronesSent: n, endsMs: state.elapsedMs + esc.durationMs, status: 'running', result: null };
  return { ok: true };
}

export function collectSortie(state: GameState): DroneOpResult {
  const dr = state.drones;
  if (!dr.sortie || dr.sortie.status !== 'returned' || !dr.sortie.result) return { ok: false, reason: 'Nothing to collect.' };
  const r = dr.sortie.result;
  state.wallet.components = add(state.wallet.components, r.components);
  if (r.cache && state.inventory.parts.length < state.inventory.cap) {
    state.inventory.parts.push(r.cache);
  }
  dr.broken = Math.min(dr.bots, dr.broken + r.broke);
  dr.sortie = null;
  return { ok: true };
}

export function repairDrones(state: GameState): DroneOpResult {
  const dr = state.drones;
  if (dr.broken <= 0) return { ok: false, reason: 'No broken drones.' };
  const cost = repairCost(dr.broken, frameTier(state));
  if (!gte(state.wallet.scrap, cost)) return { ok: false, reason: 'Not enough Scrap.' };
  state.wallet.scrap = sub(state.wallet.scrap, cost);
  dr.broken = 0;
  return { ok: true };
}

export function buildDrone(state: GameState): DroneOpResult {
  const dr = state.drones;
  if (!dr.unlocked) return { ok: false, reason: 'Drone Bay not online.' };
  if (dr.bots >= maxDrones(state)) return { ok: false, reason: 'Bay at maximum drones.' };
  const cost = buildDroneCost(dr.bots, frameTier(state));
  if (!gte(state.wallet.scrap, cost)) return { ok: false, reason: 'Not enough Scrap.' };
  state.wallet.scrap = sub(state.wallet.scrap, cost);
  dr.bots += 1;
  return { ok: true };
}

export function upgradeBay(state: GameState): DroneOpResult {
  const dr = state.drones;
  if (!dr.unlocked) return { ok: false, reason: 'Drone Bay not online.' };
  const cost = bayUpgradeCost(dr.clearance, frameTier(state));
  if (!gte(state.wallet.scrap, cost)) return { ok: false, reason: 'Not enough Scrap.' };
  state.wallet.scrap = sub(state.wallet.scrap, cost);
  dr.clearance += 1;
  return { ok: true };
}

/** Bring the bay online — called on the first Ascension. Seeds starting drones. */
export function unlockDrones(state: GameState): void {
  const dr = state.drones;
  if (dr.unlocked) return;
  dr.unlocked = true;
  if (dr.bots < START_DRONES) dr.bots = START_DRONES;
  if (dr.clearance < 1) dr.clearance = 1;
  state.meta.unlocks.drones = true;
}
